import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function ChainMaster({ token, onManageProperties }) {
  const emptyForm = {
    code: '',
    name: '',
    is_active: true
  };

  const [chains, setChains] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const loadChains = async () => {
    try {
      const res = await fetch('/api/v1/core/chains', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load chains');
        return;
      }
      setChains(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading chains');
    }
  };

  useEffect(() => {
    loadChains();
  }, [token]);

  const handleSave = async () => {
    const payload = {
      code: (form.code || '').trim(),
      name: (form.name || '').trim(),
      is_active: form.is_active
    };
    if (!payload.code || !payload.name) {
      setError('Code and Name are required');
      return;
    }

    try {
      const res = await fetch(editingId ? `/api/v1/core/chains/${editingId}` : '/api/v1/core/chains', {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to save chain');
        return;
      }
      setError('');
      setForm(emptyForm);
      setEditingId(null);
      loadChains();
    } catch {
      setError('Network error while saving chain');
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      code: row.code || '',
      name: row.name || '',
      is_active: Boolean(row.is_active)
    });
    setError('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`/api/v1/core/chains/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to deactivate chain');
        return;
      }
      setError('');
      loadChains();
    } catch {
      setError('Network error while deleting chain');
    }
  };

  const handleManageProperties = (row) => {
    localStorage.setItem('hotelConfigSelectedChainId', row.id);
    if (typeof onManageProperties === 'function') {
      onManageProperties(row.id);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Chain Master</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Chain' : 'Create Chain'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <input
            placeholder="Chain Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <input
            placeholder="Chain Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            {editingId ? 'Update Chain' : 'Create Chain'}
          </button>
          {editingId && (
            <button onClick={cancelEdit} className="btn-muted" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>Chains</h3>
      {chains.length === 0 ? (
        <p>No chains found</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Active</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {chains.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                <td style={{ padding: '0.75rem' }}>{row.code}</td>
                <td style={{ padding: '0.75rem' }}>{row.name}</td>
                <td style={{ padding: '0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleEdit(row)} style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => handleManageProperties(row)} style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', background: '#16a085', color: '#fff', cursor: 'pointer' }}>Properties</button>
                  <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', cursor: 'pointer' }}>Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ChainMaster;
